import React, { useState } from "react";
import { Delete, Plus, Percent, X as XIcon } from "lucide-react";
import { formatEUR } from "@/lib/api";
import ScontrinoGestureLabel from "./ScontrinoGestureLabel";

/**
 * Tastierino numerico SEMPRE VISIBILE per la cassa touch.
 * - Display live dell'importo digitato
 * - Bottone [+] aggiunge una riga "Prezzo libero" al conto
 * - Bottone [%] applica lo sconto (percent o €) all'ordine
 * - Bottone [x] azzera il display
 *
 * Props:
 *  - onAddFreePrice(number)      // aggiunge riga generica al conto
 *  - onApplyDiscount({mode,value}) // applica sconto ordine
 *  - currentTotal: number
 *  - compact?: boolean            // layout ridotto per aside stretti
 */
export default function InlinePOSKeypad({ onAddFreePrice, onApplyDiscount, currentTotal = 0, compact = false }) {
  const [value, setValue] = useState("");
  const numeric = parseFloat(value || 0) || 0;

  const push = (c) => {
    const v = String(value || "");
    if (c === ".") {
      if (v.includes(".")) return;
      setValue(v === "" ? "0." : v + ".");
      return;
    }
    if (v.includes(".")) {
      const dec = v.split(".")[1] || "";
      if (dec.length >= 2) return;
    }
    setValue((v + c).replace(/^0(?=\d)/, ""));
  };
  const back = () => setValue(String(value || "").slice(0, -1));
  const clear = () => setValue("");

  const doAdd = () => {
    if (numeric <= 0) return;
    onAddFreePrice?.(numeric);
    setValue("");
  };
  const doDiscountPct = () => {
    if (numeric <= 0 || numeric > 100) return;
    onApplyDiscount?.({ mode: "pct", value: numeric });
    setValue("");
  };
  const doDiscountAmt = () => {
    if (numeric <= 0) return;
    onApplyDiscount?.({ mode: "amount", value: numeric });
    setValue("");
  };

  const btnH = compact ? "h-10" : "h-11";
  const dispH = compact ? "h-11" : "h-12";
  const displayFontSize = compact ? "text-xl" : "text-2xl";

  return (
    <div className="bg-white border border-velta-border rounded-md p-2" data-testid="inline-keypad">
      <ScontrinoGestureLabel className="px-2 pt-0.5 pb-1" />
      <div className={`flex items-center justify-between mb-2 px-2 ${dispH} bg-velta-bg rounded border border-velta-border`}>
        <span className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Digita</span>
        <span data-testid="inline-keypad-display" className={`font-mono font-bold ${displayFontSize} tabular-nums`}>
          {value ? `€ ${numeric.toFixed(2)}` : <span className="text-velta-mute/40 text-sm tracking-widest">0,00</span>}
        </span>
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {[
          "7","8","9",
          "back",
          "4","5","6",
          "clear",
          "1","2","3",
          "add",
          ".","0","00",
          "discount"
        ].map((k, i) => {
          if (k === "back")
            return (
              <button
                key={"back"+i}
                data-testid="ikp-back"
                onClick={back}
                className={`${btnH} rounded bg-velta-bg border border-velta-border hover:border-velta-ink flex items-center justify-center btn-tactile active:scale-95`}
              ><Delete className="w-4 h-4" /></button>
            );
          if (k === "clear")
            return (
              <button
                key={"clear"+i}
                data-testid="ikp-clear"
                onClick={clear}
                className={`${btnH} rounded bg-velta-bg border border-velta-border hover:border-velta-error hover:text-velta-error font-mono font-bold btn-tactile`}
              ><XIcon className="w-4 h-4" /></button>
            );
          if (k === "add")
            return (
              <button
                key={"add"+i}
                data-testid="ikp-add"
                onClick={doAdd}
                disabled={numeric <= 0}
                className={`${btnH} rounded bg-velta-ink hover:bg-velta-ink/90 text-white flex items-center justify-center btn-tactile disabled:opacity-30 active:scale-95`}
                title="Aggiungi al conto come Prezzo Libero"
              ><Plus className="w-5 h-5" /></button>
            );
          if (k === "discount")
            return (
              <button
                key={"disc"+i}
                data-testid="ikp-discount"
                onClick={doDiscountPct}
                disabled={numeric <= 0 || numeric > 100}
                className={`${btnH} rounded bg-[#2F9E77] hover:bg-[#27855F] text-white flex items-center justify-center btn-tactile disabled:opacity-30 active:scale-95`}
                title="Applica come sconto percentuale al conto"
              ><Percent className="w-4 h-4" /></button>
            );
          return (
            <button
              key={k+i}
              data-testid={`ikp-${k}`}
              onClick={() => push(k)}
              className={`${btnH} rounded bg-white border border-velta-border hover:border-velta-ink font-mono font-bold text-lg btn-tactile active:scale-95`}
            >{k}</button>
          );
        })}
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px] font-mono text-velta-mute px-1">
        <button
          data-testid="ikp-discount-amt"
          onClick={doDiscountAmt}
          disabled={numeric <= 0 || numeric > currentTotal}
          className="underline underline-offset-2 hover:text-velta-ink disabled:opacity-30"
          title="Applica come sconto in € al conto"
        >
          − sconto € su totale ({formatEUR(currentTotal)})
        </button>
        <span className="opacity-60">[+] Prezzo Libero · [%] Sconto</span>
      </div>
    </div>
  );
}
